import React, { useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { qaApi } from "../../../services/api/qaApi";
import { useOptimisticUpdate } from "../../../hooks/useOptimisticUpdate";
import { formatDistanceToNow } from "date-fns";
import {
  FiArrowUp,
  FiArrowDown,
  FiCheckCircle,
  FiAward,
  FiEdit3,
  FiTrash2,
} from "react-icons/fi";
import CodeBlock from "./CodeBlock";
import UserAvatar from "../UserAvatar";
import VerifiedBadge from "../VerifiedBadge";
import ConfirmModal from "../ConfirmModal";
import "./AnswersList.css";

const Answer = React.memo(
  ({
    answer,
    postId,
    isAccepted,
    isQuestionAuthor,
    bounty,
    onUpdate,
    onDelete,
  }) => {
    // Use optimized counts from backend instead of array lengths
    const [score, setScore] = useState(
      (answer.upvoteCount || 0) - (answer.downvoteCount || 0)
    );
    // Use userVote from backend
    const [userVote, setUserVote] = useState(answer.userVote || null);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(answer.content);
    const [editCode, setEditCode] = useState(answer.code || "");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAwardBountyModal, setShowAwardBountyModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [awarding, setAwarding] = useState(false);
    const { optimisticUpdate } = useOptimisticUpdate();

    const currentUserId = useMemo(() => {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.id || user?._id || user?.userId;
    }, []);
    const isAuthor = String(answer.user?._id) === String(currentUserId);

    // Sync userVote from backend when answer updates
    React.useEffect(() => {
      if (answer.userVote !== undefined) {
        setUserVote(answer.userVote);
      }
    }, [answer.userVote]);

    // Sync score from backend when answer updates
    React.useEffect(() => {
      const newScore = (answer.upvoteCount || 0) - (answer.downvoteCount || 0);
      setScore(newScore);
    }, [answer.upvoteCount, answer.downvoteCount]);

    // Handle vote with optimistic update
    const handleVote = useCallback(
      async (voteType) => {
        const previousScore = score;
        const previousVote = userVote;

        let newScore = score;
        let newVote = voteType;

        if (userVote === voteType) {
          newScore = voteType === "upvote" ? score - 1 : score + 1;
          newVote = null;
        } else if (userVote) {
          newScore = voteType === "upvote" ? score + 2 : score - 2;
        } else {
          newScore = voteType === "upvote" ? score + 1 : score - 1;
        }

        await optimisticUpdate({
          updateFn: () => {
            setScore(newScore);
            setUserVote(newVote);
          },
          apiFn: () => qaApi.voteAnswer(postId, answer._id, voteType),
          rollbackFn: () => {
            setScore(previousScore);
            setUserVote(previousVote);
          },
        });
      },
      [score, userVote, postId, answer._id, optimisticUpdate]
    );

    // Accept answer
    const handleAccept = useCallback(async () => {
      if (!isQuestionAuthor) return;

      try {
        await qaApi.acceptAnswer(postId, answer._id);
        if (onUpdate) onUpdate();
      } catch (error) {
        alert("Failed to accept answer");
      }
    }, [isQuestionAuthor, postId, answer._id, onUpdate]);

    // Award bounty
    const handleAwardBounty = useCallback(async () => {
      setAwarding(true);
      try {
        await qaApi.awardBounty(postId, answer._id);
        setShowAwardBountyModal(false);
        if (onUpdate) onUpdate();
      } catch (error) {
        alert(error.response?.data?.message || "Failed to award bounty");
      } finally {
        setAwarding(false);
      }
    }, [postId, answer._id, onUpdate]);

    // Edit answer
    const handleEdit = useCallback(async () => {
      if (!editContent.trim()) {
        alert("Answer content cannot be empty");
        return;
      }

      // Validate minimum length (30 chars per API spec)
      if (editContent.trim().length < 30) {
        alert("Answer must be at least 30 characters long");
        return;
      }

      // Validate maximum length (10,000 chars per API spec)
      if (editContent.trim().length > 10000) {
        alert("Answer must be 10,000 characters or less");
        return;
      }

      // Validate code length if provided
      if (editCode && editCode.trim().length > 5000) {
        alert("Code snippet must be 5,000 characters or less");
        return;
      }

      try {
        await qaApi.editAnswer(postId, answer._id, {
          content: editContent.trim(),
          code: editCode.trim(),
        });
        setIsEditing(false);
        if (onUpdate) onUpdate();
      } catch (error) {
        const errorMsg =
          error.response?.data?.message || "Failed to edit answer";
        alert(errorMsg);
      }
    }, [postId, answer._id, editContent, editCode, onUpdate]);

    // Delete answer
    const handleDelete = useCallback(async () => {
      setDeleting(true);
      try {
        await qaApi.deleteAnswer(postId, answer._id);
        setShowDeleteModal(false);
        if (onDelete) onDelete(answer._id);
      } catch (error) {
        alert(error.response?.data?.message || "Failed to delete answer");
      } finally {
        setDeleting(false);
      }
    }, [postId, answer._id, onDelete]);

    return (
      <motion.div
        className={`cbx-answers-item ${
          isAccepted ? "cbx-answers-item--accepted" : ""
        }`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {isAccepted && (
          <div className="cbx-answers-item__accepted-badge">
            <FiCheckCircle /> Accepted Answer
          </div>
        )}

        <div className="cbx-answers-item__wrapper">
          {/* Vote Section */}
          <div className="cbx-answers-vote">
            <motion.button
              className={`cbx-answers-vote__btn ${
                userVote === "upvote" ? "cbx-answers-vote__btn--active" : ""
              }`}
              onClick={() => handleVote("upvote")}
              aria-label="Upvote answer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiArrowUp />
            </motion.button>
            <span className="cbx-answers-vote__score">{score}</span>
            <motion.button
              className={`cbx-answers-vote__btn ${
                userVote === "downvote"
                  ? "cbx-answers-vote__btn--active-down"
                  : ""
              }`}
              onClick={() => handleVote("downvote")}
              aria-label="Downvote answer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiArrowDown />
            </motion.button>

            {isQuestionAuthor && !isAccepted && (
              <motion.button
                className="cbx-answers-vote__accept"
                onClick={handleAccept}
                title="Accept this answer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiCheckCircle />
              </motion.button>
            )}
          </div>

          {/* Answer Body */}
          <div className="cbx-answers-body">
            {isEditing ? (
              <div className="cbx-answers-edit">
                <label className="cbx-answers-edit__label">
                  Answer Content *
                </label>
                <textarea
                  className="cbx-answers-edit__textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={6}
                  placeholder="Your answer (minimum 30 characters)..."
                  maxLength={10000}
                  required
                />
                <small
                  className={`cbx-answers-edit__count ${
                    editContent.length < 30
                      ? "cbx-answers-edit__count--warning"
                      : ""
                  }`}
                >
                  {editContent.length}/10,000 characters
                  {editContent.length > 0 && editContent.length < 30 && (
                    <span> (need {30 - editContent.length} more)</span>
                  )}
                </small>

                <label
                  className="cbx-answers-edit__label"
                  style={{ marginTop: "1rem" }}
                >
                  Code (optional)
                </label>
                <textarea
                  className="cbx-answers-edit__textarea cbx-answers-edit__textarea--code"
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value)}
                  placeholder="Paste your code here..."
                  rows={4}
                  maxLength={5000}
                />
                <small className="cbx-answers-edit__count">
                  {editCode.length}/5,000
                </small>

                <div className="cbx-answers-edit__actions">
                  <motion.button
                    onClick={handleEdit}
                    className="cbx-answers-edit__btn cbx-answers-edit__btn--save"
                    disabled={editContent.trim().length < 30}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Save
                  </motion.button>
                  <motion.button
                    onClick={() => setIsEditing(false)}
                    className="cbx-answers-edit__btn cbx-answers-edit__btn--cancel"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            ) : (
              <>
                {/* Answer Meta - All inline on one line */}
                <div className="cbx-answers-meta">
                  <UserAvatar
                    user={answer.user || { name: "Unknown User" }}
                    size="small"
                  />
                  <span className="cbx-answers-meta__name">
                    {answer.user?.name || "Unknown User"}
                  </span>
                  <VerifiedBadge user={answer.user} />
                  {answer.user?.reputation !== undefined && (
                    <span className="cbx-answers-meta__rep">
                      {answer.user.reputation} rep
                    </span>
                  )}
                  <span className="cbx-answers-meta__separator">•</span>
                  <span className="cbx-answers-meta__time">
                    {formatDistanceToNow(new Date(answer.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>

                <p className="cbx-answers-text">{answer.content}</p>

                {answer.code && <CodeBlock code={answer.code} />}

                {/* Actions */}
                <div className="cbx-answers-actions">
                  {isAuthor && (
                    <motion.button
                      onClick={() => setIsEditing(true)}
                      className="cbx-answers-actions__btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiEdit3 /> Edit
                    </motion.button>
                  )}

                  {/* Delete: Answer author OR Question author can delete */}
                  {(isAuthor || isQuestionAuthor) && (
                    <motion.button
                      onClick={() => setShowDeleteModal(true)}
                      className="cbx-answers-actions__btn cbx-answers-actions__btn--delete"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiTrash2 /> Delete
                    </motion.button>
                  )}

                  {isQuestionAuthor && bounty > 0 && !isAccepted && (
                    <motion.button
                      className="cbx-answers-actions__bounty"
                      onClick={() => setShowAwardBountyModal(true)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiAward /> Award Bounty (+{bounty})
                    </motion.button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Delete Answer Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title="Delete Answer?"
          message="Are you sure you want to delete this answer? This action cannot be undone."
          confirmText="Delete Answer"
          cancelText="Cancel"
          type="danger"
          loading={deleting}
        />

        {/* Award Bounty Modal */}
        <ConfirmModal
          isOpen={showAwardBountyModal}
          onClose={() => setShowAwardBountyModal(false)}
          onConfirm={handleAwardBounty}
          title="Award Bounty?"
          message={`Award ${bounty} reputation points to ${
            answer.user?.name || "this user"
          } for this answer? This action cannot be undone.`}
          confirmText={`Award ${bounty} Points`}
          cancelText="Cancel"
          type="info"
          loading={awarding}
        />
      </motion.div>
    );
  }
);

Answer.displayName = "Answer";

const AnswersList = ({
  postId,
  answers,
  acceptedAnswerId,
  isQuestionAuthor,
  bounty,
  onUpdate,
}) => {
  const [localAnswers, setLocalAnswers] = useState(answers);

  // Update local answers when prop changes
  React.useEffect(() => {
    setLocalAnswers(answers);
  }, [answers]);

  // Sort answers: accepted first, then by votes
  const sortedAnswers = useMemo(() => {
    return [...localAnswers].sort((a, b) => {
      if (a._id === acceptedAnswerId) return -1;
      if (b._id === acceptedAnswerId) return 1;

      // Use optimized counts from backend
      const scoreA = (a.upvoteCount || 0) - (a.downvoteCount || 0);
      const scoreB = (b.upvoteCount || 0) - (b.downvoteCount || 0);
      return scoreB - scoreA;
    });
  }, [localAnswers, acceptedAnswerId]);

  const handleDeleteAnswer = useCallback((answerId) => {
    setLocalAnswers((prev) => prev.filter((a) => a._id !== answerId));
  }, []);

  return (
    <div className="cbx-answers-list">
      <h4 className="cbx-answers-header">
        {localAnswers.length} {localAnswers.length === 1 ? "Answer" : "Answers"}
      </h4>

      {sortedAnswers.map((answer) => (
        <Answer
          key={answer._id}
          answer={answer}
          postId={postId}
          isAccepted={answer._id === acceptedAnswerId}
          isQuestionAuthor={isQuestionAuthor}
          bounty={bounty}
          onUpdate={onUpdate}
          onDelete={handleDeleteAnswer}
        />
      ))}

      {localAnswers.length === 0 && (
        <p className="cbx-answers-empty">
          No answers yet. Be the first to answer!
        </p>
      )}
    </div>
  );
};

export default React.memo(AnswersList);
